import { useState, useEffect, useCallback, useMemo } from "react";
import { useLiveData } from '../LiveDataContext';
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, Area, ComposedChart } from 'recharts';

var API = 'https://api.algrok.com';
function fmtRs(n){if(n==null||isNaN(n))return'--';return(n>=0?'+':'')+Math.round(n).toLocaleString('en-IN')+'\u20B9';}
function pc(n){return n>=0?'#00d4aa':'#ff4466';}

var SHAPE_LABELS = {
  'none': 'No Position', 'butterfly': '\uD83E\uDD8B Iron Butterfly',
  'skewed_bull': '\u2197 Bullish Skew', 'skewed_bear': '\u2198 Bearish Skew',
  'spread_bull': '\u25B2 Bull Spread', 'spread_bear': '\u25BC Bear Spread',
  'aggressive_bull': '\u26A1 Aggressive Bull', 'aggressive_bear': '\u26A1 Aggressive Bear',
  'shifted_bull': '\u21C8 Shifted Bull', 'shifted_bear': '\u21CA Shifted Bear',
};
var BRACKET_COLORS = {
  'strong_bull': '#00d4aa', 'bull': '#00d4aa', 'mild_bull': '#7dd3a8',
  'neutral': '#556677',
  'mild_bear': '#f4a0a0', 'bear': '#ff4466', 'strong_bear': '#ff4466',
};
var FACTOR_LABELS = {
  'vwap': 'VWAP', 'oi_change': 'OI Change', 'iv_skew': 'IV Skew',
  'pcr': 'PCR', 'volume_profile': 'Vol Profile', 'pivots': 'Pivots', 'orb': 'ORB',
};
var FACTOR_WEIGHTS = {
  'vwap': 20, 'oi_change': 20, 'iv_skew': 18,
  'pcr': 14, 'volume_profile': 12, 'pivots': 10, 'orb': 6,
};

function useWindowWidth() {
  var get = function() { return window.innerWidth; };
  var [w, setW] = useState(get());
  useEffect(function() { var h = function() { setW(get()); }; window.addEventListener('resize', h); return function() { window.removeEventListener('resize', h); }; }, []);
  return w;
}

export default function AMB2() {
  var [allState, setAllState] = useState(null);
  var winW = useWindowWidth();
  var isCompact = winW < 1080;
  var isTight = winW < 900;
  var [showSettings, setShowSettings] = useState(false);
  var [draft, setDraft] = useState({});
  var { getLTP, subscribeTokens } = useLiveData();

  var fetchState = useCallback(function() {
    fetch(API + '/amb2-state').then(function(r){return r.json();}).then(function(d){if(!d.error)setAllState(d);}).catch(function(){});
  }, []);
  useEffect(function() { fetchState(); var id = setInterval(fetchState, 2000); return function() { clearInterval(id); }; }, [fetchState]);

  var doApi = function(url, body) {
    fetch(API + url, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(body || {}) })
      .then(function(){fetchState();}).catch(function(){});
  };

  // Payoff at expiry chart (must be before early return — hooks can't be conditional)
  var payoff = useMemo(function(){
    if(!allState)return[];
    var st=allState;
    var lg=st.legs||[];
    var open=lg.filter(function(l){return l.status==='open'&&l.entry_price;});
    var closed=lg.filter(function(l){return l.status==='closed'&&l.entry_price&&l.exit_price;});
    var sells=open.filter(function(l){return l.side==='SELL';});
    var buysL=open.filter(function(l){return l.side==='BUY';});
    if(!sells.length||!st.atm)return[];
    // Roll cost from closed legs
    var rollCost=0;
    closed.forEach(function(l){
      if(l.side==='SELL') rollCost+=(l.entry_price-l.exit_price)*l.qty;
      else rollCost+=(l.exit_price-l.entry_price)*l.qty;
    });
    var center=st.atm;var points=[];
    for(var x=center-600;x<=center+600;x+=10){
      var pl=rollCost;
      sells.forEach(function(l){var intr=l.type==='CE'?Math.max(0,x-l.strike):Math.max(0,l.strike-x);pl+=(l.entry_price-intr)*l.qty;});
      buysL.forEach(function(l){var intr=l.type==='CE'?Math.max(0,x-l.strike):Math.max(0,l.strike-x);pl+=(intr-l.entry_price)*l.qty;});
      points.push({x:x,y:Math.round(pl)});
    }return points;
  },[allState]);
  var payoffMin=payoff.length?Math.min.apply(null,payoff.map(function(p){return p.y;})):0;
  var payoffMax=payoff.length?Math.max.apply(null,payoff.map(function(p){return p.y;})):0;

  if (!allState) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',background:'#080e1d',color:'#556677',fontFamily:"'Inter',sans-serif"}}>Loading AMB...</div>;

  var state = allState;
  var cfg = state.config || {};
  var conv = state.conviction || {};
  var scores = conv.scores || {};
  var legs = state.legs || [];
  var events = state.events || [];
  var openLegs = legs.filter(function(l){return l.status==='open';});
  var sells = openLegs.filter(function(l){return l.side==='SELL';});
  var buys = openLegs.filter(function(l){return l.side==='BUY';});
  var pnl = state.total_pnl || state.unrealized_pnl || 0;
  var realized = state.realized_pnl || 0;
  var unrealized = state.unrealized_pnl || 0;
  var shapeLabel = SHAPE_LABELS[state.current_shape] || state.current_shape;
  var bracketColor = BRACKET_COLORS[conv.bracket] || '#556677';
  var closedLegs = legs.filter(function(l){return l.status==='closed';});

  var lbl = {display:'flex',flexDirection:'column',gap:3,fontSize:10,color:'#8899aa',fontFamily:'inherit'};
  var inp = {background:'#0a0f1e',color:'#e2e8f0',border:'1px solid rgba(26,45,74,0.8)',borderRadius:4,padding:'4px 8px',fontSize:11,fontFamily:'inherit',width:'100%'};
  var S = function(key){return function(v){setDraft(function(d){var n={};n[key]=v;return Object.assign({},d,n);});};};

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',background:'#080e1d',color:'#e2e8f0',fontFamily:"'Inter',sans-serif",overflow:'hidden'}}>

      {/* Header */}
      <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 14px',background:'#0d1526',borderBottom:'1px solid rgba(26,45,74,0.6)',flexShrink:0,flexWrap:'wrap'}}>
        <div>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <div style={{fontSize:15,fontWeight:800}}>Adaptive Morphing Butterfly 2</div>
            {state.is_expiry_day&&<div style={{padding:'2px 8px',borderRadius:4,fontSize:9,fontWeight:800,background:'rgba(251,191,36,0.18)',color:'#fbbf24',border:'1px solid rgba(251,191,36,0.4)'}}>⚠ EXPIRY DAY</div>}
          </div>
          <div style={{fontSize:9,color:'#556677'}}>{cfg.index} · {cfg.lots}L · Entry {cfg.entry_time}{state.is_expiry_day?(' · Def-roll '+(cfg.expiry_day_defensive_forced_roll||50)+'pts · '+(cfg.expiry_day_trade_expiry||'next_week').replace('_',' ')):''}</div>
        </div>
        <div style={{marginLeft:'auto',textAlign:'right'}}>
          <div style={{fontSize:17,fontWeight:900,color:pc(pnl)}}>{fmtRs(pnl)}</div>
          <div style={{fontSize:9,color:'#556677'}}>R:{fmtRs(realized)} U:{fmtRs(unrealized)} | Credit: {fmtRs(state.entry_premium)}</div>
        </div>
        <div style={{padding:'3px 8px',borderRadius:4,fontSize:10,fontWeight:800,background:state.active?'rgba(0,212,170,0.12)':state.enabled?'rgba(251,191,36,0.12)':'rgba(100,100,120,0.12)',color:state.active?'#00d4aa':state.enabled?'#fbbf24':'#8899aa'}}>
          {state.active?'\u25CF ACTIVE':state.enabled?'\u25CB ENABLED':'\u25CB DISABLED'}
        </div>
        {!state.active && <button onClick={function(){doApi(state.enabled?'/amb2-disable':'/amb2-enable');}} style={{padding:'5px 14px',borderRadius:4,fontSize:11,fontWeight:800,cursor:'pointer',fontFamily:'inherit',background:state.enabled?'rgba(251,191,36,0.15)':'rgba(0,212,170,0.15)',color:state.enabled?'#fbbf24':'#00d4aa',border:'1px solid '+(state.enabled?'rgba(251,191,36,0.4)':'rgba(0,212,170,0.4)')}}>{state.enabled?'Disable':'Enable'}</button>}
        {!state.active && <button onClick={function(){doApi('/amb2-enter');}} style={{padding:'5px 14px',borderRadius:4,fontSize:11,fontWeight:800,cursor:'pointer',fontFamily:'inherit',background:'rgba(14,165,233,0.15)',color:'#0ea5e9',border:'1px solid rgba(14,165,233,0.4)'}}>Force Enter</button>}
        {state.active && <button onClick={function(){if(window.confirm('Exit AMB2 positions?'))doApi('/amb2-exit',{reason:'Manual exit'});}} style={{padding:'5px 14px',borderRadius:4,fontSize:11,fontWeight:800,cursor:'pointer',fontFamily:'inherit',background:'rgba(255,68,102,0.15)',color:'#ff4466',border:'1px solid rgba(255,68,102,0.4)'}}>Exit</button>}
        <button onClick={function(){setDraft(cfg);setShowSettings(!showSettings);}} style={{padding:'5px 10px',borderRadius:4,fontSize:11,cursor:'pointer',fontFamily:'inherit',background:'rgba(14,165,233,0.12)',color:'#0ea5e9',border:'1px solid rgba(14,165,233,0.3)',fontWeight:700}}>{'\u2699'}</button>
      </div>

      {/* Settings Modal */}
      {showSettings&&<div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}} onClick={function(){setShowSettings(false);}}>
        <div onClick={function(e){e.stopPropagation();}} style={{background:'#0d1526',borderRadius:12,padding:24,width:480,maxHeight:'80vh',overflowY:'auto',border:'1px solid rgba(26,45,74,0.6)'}}>
          <div style={{fontSize:14,fontWeight:800,marginBottom:16}}>AMB Conviction Settings</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <label style={lbl}>Entry Time<input type="text" value={draft.entry_time||cfg.entry_time} onChange={function(e){S('entry_time')(e.target.value);}} style={inp}/></label>
            <label style={lbl}>Lots<input type="number" min="1" value={draft.lots!=null?draft.lots:cfg.lots} onChange={function(e){S('lots')(Math.max(1,+e.target.value));}} style={inp}/></label>
            <label style={lbl}>Exit Weekly<input type="text" value={draft.exit_time_weekly||cfg.exit_time_weekly} onChange={function(e){S('exit_time_weekly')(e.target.value);}} style={inp}/></label>
            <label style={lbl}>Product<select value={draft.product||cfg.product||'MIS'} onChange={function(e){S('product')(e.target.value);}} style={inp}><option value="MIS">MIS</option><option value="NRML">NRML</option></select></label>
            <label style={lbl}>Hedge Mode<select value={draft.hedge_mode||cfg.hedge_mode} onChange={function(e){S('hedge_mode')(e.target.value);}} style={inp}><option value="auto_oi">Auto + OI</option><option value="oi_walls">OI Walls</option><option value="fixed">Fixed</option><option value="none">No hedge</option></select></label>
            {(draft.hedge_mode||cfg.hedge_mode)==='fixed'&&<label style={lbl}>Wing Width (pts)<input type="number" step="50" value={draft.fixed_wing_width!=null?draft.fixed_wing_width:cfg.fixed_wing_width||400} onChange={function(e){S('fixed_wing_width')(+e.target.value);}} style={inp}/></label>}
            <label style={lbl}>Profit Target %<input type="number" value={draft.profit_target_pct!=null?draft.profit_target_pct:cfg.profit_target_pct} onChange={function(e){S('profit_target_pct')(+e.target.value);}} style={inp}/></label>
            <label style={lbl}>Max Loss x<input type="number" step="0.1" value={draft.max_loss_multiple!=null?draft.max_loss_multiple:cfg.max_loss_multiple} onChange={function(e){S('max_loss_multiple')(+e.target.value);}} style={inp}/></label>
            <label style={lbl}>Roll Distance<input type="number" step="50" value={draft.roll_distance!=null?draft.roll_distance:cfg.roll_distance} onChange={function(e){S('roll_distance')(+e.target.value);}} style={inp}/></label>
            <label style={lbl}>Leg SL Multiple<input type="number" step="0.1" value={draft.leg_sl_multiple!=null?draft.leg_sl_multiple:cfg.leg_sl_multiple} onChange={function(e){S('leg_sl_multiple')(+e.target.value);}} style={inp}/></label>
          </div>
          <div style={{fontSize:10,fontWeight:700,color:'#556677',textTransform:'uppercase',borderTop:'1px solid rgba(26,45,74,0.3)',paddingTop:10,marginTop:12,marginBottom:8}}>Profit Management</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <label style={lbl}>MTM SL ₹/lot <span style={{color:'#ff4466',fontSize:9}}>(0=off)</span><input type="number" min={0} value={draft.mtm_sl_per_lot!=null?draft.mtm_sl_per_lot:cfg.mtm_sl_per_lot||0} onChange={function(e){S('mtm_sl_per_lot')(parseInt(e.target.value)||0);}} style={inp}/></label>
            <label style={lbl}>Target ₹/lot<input type="number" min={0} value={draft.target_profit_per_lot!=null?draft.target_profit_per_lot:cfg.target_profit_per_lot||0} onChange={function(e){S('target_profit_per_lot')(parseInt(e.target.value)||0);}} style={inp}/></label>
            <label style={lbl}>Protect ₹/lot<input type="number" min={0} value={draft.protect_profit_per_lot!=null?draft.protect_profit_per_lot:cfg.protect_profit_per_lot||0} onChange={function(e){S('protect_profit_per_lot')(parseInt(e.target.value)||0);}} style={inp}/></label>
            <label style={lbl}>Protect Level ₹/lot<input type="number" min={0} value={draft.protect_profit_level!=null?draft.protect_profit_level:cfg.protect_profit_level||0} onChange={function(e){S('protect_profit_level')(parseInt(e.target.value)||0);}} style={inp}/></label>
            <label style={lbl}>Trail Step ₹/lot<input type="number" min={0} value={draft.trail_protect_per_lot!=null?draft.trail_protect_per_lot:cfg.trail_protect_per_lot||0} onChange={function(e){S('trail_protect_per_lot')(parseInt(e.target.value)||0);}} style={inp}/></label>
          </div>
          <div style={{marginTop:14,paddingTop:12,borderTop:'1px solid rgba(255,165,0,0.2)'}}>
            <div style={{fontSize:11,fontWeight:700,color:'#fbbf24',marginBottom:8}}>⚠️ Expiry Day Settings <span style={{color:'#8899aa',fontWeight:400,fontSize:9}}>(active only on detected expiry day)</span></div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
              <label style={lbl}>Calc Expiry
                <select value={draft.expiry_day_calc_expiry||cfg.expiry_day_calc_expiry||'next_week'} onChange={function(e){S('expiry_day_calc_expiry')(e.target.value);}} style={inp}>
                  <option value="current_week">Current week</option>
                  <option value="next_week">Next week</option>
                </select>
              </label>
              <label style={lbl}>Trade Expiry
                <select value={draft.expiry_day_trade_expiry||cfg.expiry_day_trade_expiry||'next_week'} onChange={function(e){S('expiry_day_trade_expiry')(e.target.value);}} style={inp}>
                  <option value="current_week">Current week</option>
                  <option value="next_week">Next week</option>
                </select>
              </label>
              <label style={lbl}>Defensive Forced Roll
                <select value={draft.expiry_day_defensive_forced_roll!=null?draft.expiry_day_defensive_forced_roll:(cfg.expiry_day_defensive_forced_roll||50)} onChange={function(e){S('expiry_day_defensive_forced_roll')(parseInt(e.target.value));}} style={inp}>
                  <option value={50}>50 (default)</option>
                  <option value={100}>100</option>
                </select>
              </label>
              <label style={lbl}>Roll Distance
                <select value={draft.expiry_day_roll_distance!=null?draft.expiry_day_roll_distance:(cfg.expiry_day_roll_distance||100)} onChange={function(e){S('expiry_day_roll_distance')(parseInt(e.target.value));}} style={inp}>
                  <option value={50}>50</option>
                  <option value={100}>100 (default)</option>
                </select>
              </label>
              <label style={lbl}>Hedge Mode
                <select value={draft.expiry_day_hedge_mode||cfg.expiry_day_hedge_mode||'auto_oi'} onChange={function(e){S('expiry_day_hedge_mode')(e.target.value);}} style={inp}>
                  <option value="auto_oi">Auto + OI</option>
                  <option value="oi_walls">OI Walls</option>
                  <option value="fixed">Fixed</option>
                  <option value="none">No hedge</option>
                </select>
              </label>
              <label style={lbl}>Exit Time (0DTE)
                <input type="text" placeholder="15:10" value={draft.exit_time_0dte||cfg.exit_time_0dte||'15:10'} onChange={function(e){S('exit_time_0dte')(e.target.value);}} style={inp}/>
              </label>
              {(draft.expiry_day_hedge_mode||cfg.expiry_day_hedge_mode||'auto_oi')==='fixed'&&<label style={Object.assign({},lbl,{gridColumn:'1 / -1'})}>Hedge Wing Width (pts) <span style={{color:'#8899aa',fontSize:9}}>used when Hedge Mode = Fixed</span>
                <input type="number" step="50" value={draft.expiry_day_fixed_wing_width!=null?draft.expiry_day_fixed_wing_width:cfg.expiry_day_fixed_wing_width||400} onChange={function(e){S('expiry_day_fixed_wing_width')(+e.target.value);}} style={inp}/>
              </label>}
            </div>
            <div style={{fontSize:9,color:'#556677',marginTop:6,fontStyle:'italic'}}>Exit Time (0DTE) applies only when Trade Expiry = Current Week. With Trade Expiry = Next Week, weekly exit time applies (DTE will be 7 days).</div>
          </div>
          <div style={{display:'flex',gap:10,marginTop:20}}>
            <button onClick={function(){setShowSettings(false);}} style={{flex:1,padding:10,borderRadius:6,fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit',background:'rgba(100,100,120,0.1)',color:'#8899aa',border:'1px solid rgba(100,100,120,0.3)'}}>Cancel</button>
            <button onClick={function(){doApi('/amb2-config',draft);setShowSettings(false);}} style={{flex:1,padding:10,borderRadius:6,fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit',background:'rgba(0,212,170,0.15)',color:'#00d4aa',border:'1px solid rgba(0,212,170,0.4)'}}>Save</button>
          </div>
        </div>
      </div>}

      <div style={{flex:1,overflowY:'auto',padding:'10px 14px'}}>

        {/* Conviction Dashboard — 2-column like RatioSpread */}
        <div style={{display:'grid',gridTemplateColumns:isCompact?'150px 1fr':'200px 1fr',gap:14,marginBottom:14}}>
          {/* Score gauge */}
          <div style={{background:'rgba(13,21,38,0.8)',borderRadius:8,padding:16,border:'1px solid rgba(26,45,74,0.4)',textAlign:'center'}}>
            <div style={{fontSize:9,color:'#556677',fontWeight:700,textTransform:'uppercase',marginBottom:4}}>CONVICTION</div>
            {/* 180° Meter: -100 (left, bear) to +100 (right, bull) */}
            {(function(){
              var score=conv.conviction||0;
              var pct=(score+100)/200;
              var r=52,cx=60,cy=56,sw=10;
              var segs=[
                {start:180,end:206,color:'#991b1b'},
                {start:206,end:232,color:'#e24b4a'},
                {start:232,end:258,color:'#f97316'},
                {start:258,end:270,color:'#fbbf24'},
                {start:270,end:282,color:'#9ca3af'},
                {start:282,end:308,color:'#fbbf24'},
                {start:308,end:334,color:'#4ade80'},
                {start:334,end:360,color:'#15803d'},
              ];
              function toRad(d){return d*Math.PI/180;}
              function arc(s,e){
                var x1=cx+r*Math.cos(toRad(s)),y1=cy+r*Math.sin(toRad(s));
                var x2=cx+r*Math.cos(toRad(e)),y2=cy+r*Math.sin(toRad(e));
                return'M '+x1.toFixed(2)+' '+y1.toFixed(2)+' A '+r+' '+r+' 0 0 1 '+x2.toFixed(2)+' '+y2.toFixed(2);
              }
              var needleA=180+pct*180;
              var nl=r-12;
              var nx=cx+nl*Math.cos(toRad(needleA));
              var ny=cy+nl*Math.sin(toRad(needleA));
              return<svg viewBox="0 0 120 68" style={{width:170,display:'block',margin:'0 auto'}}>
                {segs.map(function(s,i){return<path key={i} d={arc(s.start,s.end)} fill="none" stroke={s.color} strokeWidth={sw} strokeLinecap="butt" opacity="0.85"/>;})};
                <line x1={cx} y1={cy} x2={nx.toFixed(2)} y2={ny.toFixed(2)} stroke="#e2e8f0" strokeWidth="2.5" strokeLinecap="round"/>
                <circle cx={cx} cy={cy} r="4" fill="#1a2d4a"/>
                <circle cx={cx} cy={cy} r="2.5" fill="#e2e8f0"/>
                <text x="6" y={cy+11} textAnchor="start" fill="#e24b4a" fontSize="6" fontWeight="700" fontFamily="inherit">BEAR</text>
                <text x="114" y={cy+11} textAnchor="end" fill="#15803d" fontSize="6" fontWeight="700" fontFamily="inherit">BULL</text>
              </svg>;
            })()}
            <div style={{fontSize:isTight?22:28,fontWeight:900,color:bracketColor,lineHeight:1,marginTop:2}}>{conv.conviction||0}</div>
            <div style={{fontSize:11,fontWeight:800,color:bracketColor,marginTop:3}}>{(conv.bracket||'neutral').replace('_',' ').toUpperCase()}</div>
            {/* Shape indicator */}
            <div style={{fontSize:10,color:'#8899aa',marginTop:8,padding:'4px 8px',borderRadius:4,background:'rgba(26,45,74,0.2)'}}>{shapeLabel}</div>
            {/* ORB */}
            <div style={{fontSize:9,color:'#556677',marginTop:6}}>
              ORB: {conv.orb&&conv.orb.set?Math.round(conv.orb.high)+' / '+Math.round(conv.orb.low):'Collecting...'}
            </div>
          </div>

          {/* Signal breakdown */}
          <div style={{background:'rgba(13,21,38,0.8)',borderRadius:8,padding:16,border:'1px solid rgba(26,45,74,0.4)'}}>
            <div style={{fontSize:9,color:'#556677',fontWeight:700,textTransform:'uppercase',marginBottom:8}}>SIGNAL BREAKDOWN</div>
            {Object.keys(FACTOR_LABELS).map(function(key){
              var rawScore=scores[key]||0;
              var weight=FACTOR_WEIGHTS[key]||0;
              var contrib=Math.round(rawScore*weight*10)/10;
              var pct=Math.abs(rawScore)*100;
              var detail='';
              if(key==='vwap')detail='VWAP: '+(state.vwap||'--');
              if(key==='pcr'&&conv.scores)detail='PCR score';
              if(key==='iv_skew')detail='CE vs PE IV';
              if(key==='oi_change')detail='OI delta rate';
              if(key==='pivots'&&state.pivots&&state.pivots.P)detail='P='+state.pivots.P;
              if(key==='orb')detail=conv.orb&&conv.orb.set?(rawScore>0?'Above':'Below'):'Waiting';
              if(key==='volume_profile')detail=conv.vol_profile?'A:'+Math.round((conv.vol_profile.above_vwap||0)/1000)+'k B:'+Math.round((conv.vol_profile.below_vwap||0)/1000)+'k':'';
              return<div key={key} style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                <div style={{width:90,fontSize:10,color:'#8899aa'}}>{FACTOR_LABELS[key]}</div>
                <div style={{flex:1,height:6,background:'rgba(26,45,74,0.3)',borderRadius:3,position:'relative'}}>
                  <div style={{position:'absolute',left:'50%',top:0,width:1,height:'100%',background:'#1a2d4a'}}/>
                  <div style={{position:'absolute',left:rawScore>=0?'50%':'auto',right:rawScore<0?'50%':'auto',width:Math.min(50,pct/2)+'%',height:'100%',borderRadius:3,background:rawScore>0?'#00d4aa':rawScore<0?'#ff4466':'transparent',transition:'width 0.3s'}}/>
                </div>
                <div style={{width:35,fontSize:11,fontWeight:700,color:contrib>0?'#00d4aa':contrib<0?'#ff4466':'#556677',textAlign:'right'}}>{contrib>0?'+':''}{contrib}</div>
                <div style={{width:90,fontSize:9,color:'#556677',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{detail}</div>
              </div>;
            })}
          </div>
        </div>

        {/* Conviction Timeline Chart — Recharts */}
        {(conv.history||[]).length>1&&<div style={{background:'rgba(13,21,38,0.8)',borderRadius:8,padding:16,border:'1px solid rgba(26,45,74,0.4)',marginBottom:14}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
            <div style={{fontSize:9,color:'#556677',fontWeight:700,textTransform:'uppercase'}}>CONVICTION TIMELINE</div>
            <div style={{fontSize:9,color:'#3d5068'}}>{(conv.history||[]).length} pts &middot; {cfg.check_interval_secs||10}s</div>
          </div>
          {function(){
            var hist=conv.history||[];
            var maxS=Math.max(60,Math.max.apply(null,hist.map(function(h){return Math.abs(h.score);})));
            // Gradient stops based on domain — map score positions to 0-1 range (top=+maxS, bottom=-maxS)
            var p20=((maxS-20)/(2*maxS)*100).toFixed(1); // +20 position
            var p0='50';                                   // 0 position
            var n20=((maxS+20)/(2*maxS)*100).toFixed(1);  // -20 position

            var chartData=hist.map(function(h){
              return{
                time:h.time, score:h.score, bracket:h.bracket||'',
                pos:h.score>=0?h.score:0,
                neg:h.score<0?h.score:0,
                darkGreen:h.score>20?h.score:h.score>0?0:0,
                lightGreen:h.score>0?Math.min(h.score,20):0,
                orange:h.score<0?Math.max(h.score,-20):0,
                red:h.score<-20?h.score:h.score<0?0:0,
              };
            });
            var convTooltip=function(props){
              if(!props.active||!props.payload||!props.payload.length)return null;
              var d=props.payload[0].payload;
              var sc=d.score;
              var col=sc>20?'#00b894':sc>0?'#55efc4':sc>-20?'#ffa502':'#ff4466';
              return<div style={{background:'#0d1526',border:'1px solid rgba(26,45,74,0.6)',borderRadius:6,padding:'8px 12px',fontSize:11,minWidth:140}}>
                <div style={{fontWeight:700,marginBottom:4,color:'#e2e8f0'}}>{d.time}</div>
                <div style={{color:col,fontWeight:700,fontSize:16}}>{sc>=0?'+':''}{sc}</div>
                {d.bracket&&<div style={{color:'#556677',marginTop:2,fontSize:10}}>{d.bracket}</div>}
              </div>;
            };
            return<ResponsiveContainer width="100%" height={160}>
              <ComposedChart data={chartData} margin={{top:10,right:10,bottom:5,left:5}}>
                <defs>
                  <linearGradient id="lineColor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00b894"/>
                    <stop offset={p20+'%'} stopColor="#00b894"/>
                    <stop offset={p20+'%'} stopColor="#55efc4"/>
                    <stop offset={p0+'%'} stopColor="#55efc4"/>
                    <stop offset={p0+'%'} stopColor="#ffa502"/>
                    <stop offset={n20+'%'} stopColor="#ffa502"/>
                    <stop offset={n20+'%'} stopColor="#ff4466"/>
                    <stop offset="100%" stopColor="#ff4466"/>
                  </linearGradient>
                  <linearGradient id="fillGreen" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00b894" stopOpacity={0.25}/>
                    <stop offset="100%" stopColor="#55efc4" stopOpacity={0.05}/>
                  </linearGradient>
                  <linearGradient id="fillRed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ffa502" stopOpacity={0.05}/>
                    <stop offset="100%" stopColor="#ff4466" stopOpacity={0.25}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="time" tick={{fontSize:8,fill:'#3d4f63'}} interval={Math.max(1,Math.floor(chartData.length/8))} axisLine={false} tickLine={false}/>
                <YAxis domain={[-maxS,maxS]} tick={{fontSize:8,fill:'#3d4f63'}} width={30} axisLine={false} tickLine={false}/>
                <ReferenceLine y={0} stroke="#1a2d4a" strokeDasharray="3 3"/>
                <ReferenceLine y={20} stroke="rgba(0,212,170,0.2)" strokeDasharray="2 2"/>
                <ReferenceLine y={-20} stroke="rgba(255,68,102,0.2)" strokeDasharray="2 2"/>
                <Tooltip content={convTooltip} cursor={{stroke:'#556677',strokeDasharray:'3 3'}}/>
                <Area type="monotone" dataKey="pos" fill="url(#fillGreen)" stroke="none" baseLine={0} isAnimationActive={false}/>
                <Area type="monotone" dataKey="neg" fill="url(#fillRed)" stroke="none" baseLine={0} isAnimationActive={false}/>
                <Line type="monotone" dataKey="score" stroke="url(#lineColor)" strokeWidth={2} dot={false} isAnimationActive={false}/>
              </ComposedChart>
            </ResponsiveContainer>;
          }()}
        </div>}

        {/* Shape + Market Data */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:8,marginBottom:12}}>
          <div style={{background:'rgba(13,21,38,0.8)',borderRadius:6,padding:'8px 10px',border:'1px solid rgba(26,45,74,0.25)'}}>
            <div style={{fontSize:9,color:'#556677',fontWeight:700}}>SHAPE</div>
            <div style={{fontSize:13,fontWeight:800,color:'#e2e8f0'}}>{shapeLabel}</div>
          </div>
          <div style={{background:'rgba(13,21,38,0.8)',borderRadius:6,padding:'8px 10px',border:'1px solid rgba(26,45,74,0.25)'}}>
            <div style={{fontSize:9,color:'#556677',fontWeight:700}}>SPOT / ATM</div>
            <div style={{fontSize:13,fontWeight:800,color:'#e2e8f0'}}>{state.spot?state.spot.toFixed(0):'--'} / {state.atm||'--'}</div>
          </div>
          <div style={{background:'rgba(13,21,38,0.8)',borderRadius:6,padding:'8px 10px',border:'1px solid rgba(26,45,74,0.25)'}}>
            <div style={{fontSize:9,color:'#556677',fontWeight:700}}>VWAP</div>
            <div style={{fontSize:13,fontWeight:800,color:state.vwap&&state.spot?(state.spot>=state.vwap?'#00d4aa':'#ff4466'):'#e2e8f0'}}>{state.vwap||'--'}</div>
          </div>
          <div style={{background:'rgba(13,21,38,0.8)',borderRadius:6,padding:'8px 10px',border:'1px solid rgba(26,45,74,0.25)'}}>
            <div style={{fontSize:9,color:'#556677',fontWeight:700}}>NET DELTA</div>
            <div style={{fontSize:13,fontWeight:800,color:Math.abs(state.net_delta||0)<0.1?'#556677':pc(state.net_delta||0)}}>{state.net_delta?state.net_delta.toFixed(3):'0'}</div>
          </div>
        </div>

        {/* Pivots */}
        {state.pivots && state.pivots.P && (
          <div style={{display:'flex',gap:isCompact?5:8,marginBottom:12,fontSize:isTight?8:9,flexWrap:'wrap',flexWrap:'wrap'}}>
            {['S3','S2','S1','P','R1','R2','R3'].map(function(k){
              var val=state.pivots[k];
              if(!val)return null;
              var isSupport=k.startsWith('S');
              var isPivot=k==='P';
              var spotAbove=state.spot&&state.spot>val;
              return <div key={k} style={{padding:'3px 8px',borderRadius:3,background:isPivot?'rgba(14,165,233,0.1)':isSupport?'rgba(0,212,170,0.06)':'rgba(255,68,102,0.06)',border:'1px solid '+(isPivot?'rgba(14,165,233,0.3)':isSupport?'rgba(0,212,170,0.2)':'rgba(255,68,102,0.2)')}}>
                <span style={{color:isPivot?'#0ea5e9':isSupport?'#00d4aa':'#ff4466',fontWeight:700}}>{k}</span>
                <span style={{color:'#8899aa',marginLeft:4}}>{val}</span>
              </div>;
            })}
          </div>
        )}

        {/* Payoff at Expiry — Shift mode only */}
        {payoff.length>0&&(
          <div style={{background:'#0d1526',borderRadius:6,padding:12,marginBottom:12,border:'1px solid rgba(26,45,74,0.4)'}}>
            <div style={{fontSize:10,fontWeight:800,color:'#556677',textTransform:'uppercase',marginBottom:8}}>Payoff at Expiry</div>
            <svg viewBox="0 0 720 200" style={{width:'100%',height:120}}>
              <defs><linearGradient id="ambProfG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00d4aa" stopOpacity="0.3"/><stop offset="100%" stopColor="#00d4aa" stopOpacity="0"/></linearGradient></defs>
              {function(){var range=payoffMax-payoffMin||1;var zy=10+((payoffMax-0)/range)*180;return<line x1="40" y1={zy} x2="690" y2={zy} stroke="rgba(100,120,140,0.4)"/>;}()}
              <polyline fill="none" stroke="#0ea5e9" strokeWidth="2" points={payoff.map(function(p,i){var x=40+(i/(payoff.length-1))*650;var range=payoffMax-payoffMin||1;var y=10+((payoffMax-p.y)/range)*180;return x+','+y;}).join(' ')}/>
              {function(){var range=payoffMax-payoffMin||1;var zy=10+((payoffMax-0)/range)*180;var pts='';payoff.forEach(function(p,i){var x=40+(i/(payoff.length-1))*650;var y=10+((payoffMax-p.y)/range)*180;if(p.y>=0)pts+=x+','+y+' ';});if(pts){var first=pts.split(' ')[0];var last=pts.trim().split(' ').pop();return<polygon fill="url(#ambProfG)" points={pts+' '+last.split(',')[0]+','+zy+' '+first.split(',')[0]+','+zy}/>;}return null;}()}
              {state.spot>0&&function(){var minX=payoff[0].x,maxX=payoff[payoff.length-1].x;var sx=40+((state.spot-minX)/(maxX-minX))*650;if(sx>=40&&sx<=690)return<g><line x1={sx} y1="5" x2={sx} y2="195" stroke="#fbbf24" strokeWidth="1" strokeDasharray="4"/><text x={sx} y="8" fill="#fbbf24" fontSize="8" textAnchor="middle">SPOT</text></g>;return null;}()}
              {state.atm>0&&function(){var minX=payoff[0].x,maxX=payoff[payoff.length-1].x;var ax=40+((state.atm-minX)/(maxX-minX))*650;if(ax>=40&&ax<=690)return<line x1={ax} y1="10" x2={ax} y2="190" stroke="rgba(14,165,233,0.3)" strokeDasharray="2"/>;return null;}()}
              <text x="35" y="15" fill="#556677" fontSize="8" textAnchor="end">{payoffMax>=1000?(payoffMax/1000).toFixed(1)+'k':payoffMax}</text>
              <text x="35" y="195" fill="#556677" fontSize="8" textAnchor="end">{payoffMin<=-1000?(payoffMin/1000).toFixed(1)+'k':payoffMin}</text>
              {[0,0.25,0.5,0.75,1].map(function(f){var idx=Math.floor(f*(payoff.length-1));return<text key={f} x={40+f*650} y="198" fill="#556677" fontSize="8" textAnchor="middle">{payoff[idx]?payoff[idx].x:''}</text>;})}
            </svg>
          </div>
        )}

        {/* Legs Table */}
        {openLegs.length > 0 && (
          <div style={{marginBottom:12}}>
            <div style={{fontSize:10,fontWeight:800,color:'#556677',marginBottom:6}}>LEGS ({openLegs.length} open · {state.adjustment_count} adjustments)</div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
              <thead><tr style={{background:'#0d1526'}}>
                {['Label','Symbol','Side','Strike','Entry','LTP','P&L'].map(function(h){return <th key={h} style={{padding:'5px 6px',textAlign:'left',color:'#556677',fontWeight:700,borderBottom:'1px solid rgba(26,45,74,0.4)'}}>{h}</th>;})}
              </tr></thead>
              <tbody>{openLegs.map(function(leg,i){
                var legPnl=0;
                if(leg.entry_price&&leg.ltp){
                  legPnl=leg.side==='SELL'?(leg.entry_price-leg.ltp)*leg.qty:(leg.ltp-leg.entry_price)*leg.qty;
                }
                return <tr key={i} style={{borderBottom:'1px solid rgba(26,45,74,0.12)'}}>
                  <td style={{padding:'5px 6px',color:'#8899aa'}}>{leg.label}</td>
                  <td style={{padding:'5px 6px',color:'#e2e8f0',fontWeight:700}}>{(leg.symbol||'').slice(-15)} <span style={{fontSize:8,padding:'0 3px',borderRadius:2,background:leg.type==='CE'?'rgba(0,212,170,0.12)':'rgba(255,68,102,0.12)',color:leg.type==='CE'?'#00d4aa':'#ff4466'}}>{leg.type}</span></td>
                  <td style={{padding:'5px 6px',color:leg.side==='SELL'?'#ff4466':'#00d4aa',fontWeight:700}}>{leg.side}</td>
                  <td style={{padding:'5px 6px',color:'#8899aa'}}>{leg.strike}</td>
                  <td style={{padding:'5px 6px',color:'#e2e8f0'}}>{'\u20B9'}{leg.entry_price?leg.entry_price.toFixed(1):'--'}</td>
                  <td style={{padding:'5px 6px',color:'#0ea5e9',fontWeight:700}}>{'\u20B9'}{leg.ltp?leg.ltp.toFixed(1):'--'}</td>
                  <td style={{padding:'5px 6px',color:pc(legPnl),fontWeight:800}}>{fmtRs(legPnl)}</td>
                </tr>;
              })}</tbody>
            </table>
          </div>
        )}

        {/* Closed Legs */}
        {closedLegs.length > 0 && (
          <div style={{marginBottom:12}}>
            <div style={{fontSize:10,fontWeight:800,color:'#556677',marginBottom:6}}>CLOSED LEGS ({closedLegs.length}) — Realized: <span style={{color:pc(realized)}}>{fmtRs(realized)}</span></div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
              <thead><tr style={{background:'#0d1526'}}>
                {['Label','Symbol','Side','Strike','Entry','Exit','P&L','Time'].map(function(h){return <th key={h} style={{padding:'5px 6px',textAlign:'left',color:'#3d4f63',fontWeight:700,borderBottom:'1px solid rgba(26,45,74,0.25)'}}>{h}</th>;})}
              </tr></thead>
              <tbody>{closedLegs.map(function(leg,i){
                var legPnl=0;
                if(leg.entry_price&&leg.exit_price){
                  legPnl=leg.side==='SELL'?(leg.entry_price-leg.exit_price)*leg.qty:(leg.exit_price-leg.entry_price)*leg.qty;
                }
                return <tr key={i} style={{borderBottom:'1px solid rgba(26,45,74,0.08)',opacity:0.7}}>
                  <td style={{padding:'4px 6px',color:'#556677'}}>{leg.label}</td>
                  <td style={{padding:'4px 6px',color:'#8899aa'}}>{(leg.symbol||'').slice(-15)} <span style={{fontSize:8,padding:'0 3px',borderRadius:2,background:leg.type==='CE'?'rgba(0,212,170,0.08)':'rgba(255,68,102,0.08)',color:leg.type==='CE'?'#00d4aa':'#ff4466'}}>{leg.type}</span></td>
                  <td style={{padding:'4px 6px',color:leg.side==='SELL'?'#ff4466':'#00d4aa'}}>{leg.side}</td>
                  <td style={{padding:'4px 6px',color:'#556677'}}>{leg.strike}</td>
                  <td style={{padding:'4px 6px',color:'#8899aa'}}>{'\u20B9'}{leg.entry_price?leg.entry_price.toFixed(1):'--'}</td>
                  <td style={{padding:'4px 6px',color:'#e2e8f0',fontWeight:700}}>{'\u20B9'}{leg.exit_price?leg.exit_price.toFixed(1):'--'}</td>
                  <td style={{padding:'4px 6px',color:pc(legPnl),fontWeight:800}}>{fmtRs(legPnl)}</td>
                  <td style={{padding:'4px 6px',color:'#3d4f63',fontSize:9}}>{leg.exit_time||''}</td>
                </tr>;
              })}</tbody>
            </table>
          </div>
        )}

        {/* Activity Log */}
        <div style={{marginTop:10,paddingBottom:10}}>
          <div style={{fontSize:10,fontWeight:800,color:'#556677',marginBottom:6}}>ACTIVITY LOG ({events.length})</div>
          {events.slice().reverse().slice(0,40).map(function(l,i){
            return <div key={i} style={{display:'flex',gap:8,fontSize:10,marginBottom:2}}>
              <span style={{color:'#3d4f63',flexShrink:0,fontSize:9}}>{l.ts}</span>
              <span style={{color:l.type==='error'?'#ff4466':l.type==='success'?'#00d4aa':l.type==='warning'?'#fbbf24':'#6b7a8d'}}>{l.msg}</span>
            </div>;
          })}
        </div>
      </div>
    </div>
  );
}
